
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { PrayerImage } from '../../../lib/supabase';
import ImageUploadForm from './components/ImageUploadForm';
import ImageList from './components/ImageList';
import AdminHeader from '../../../components/feature/AdminHeader';

export default function AdminPrayerPage() {
  const [images, setImages] = useState<PrayerImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('prayer_images')
        .select('*')
        .order('week_date', { ascending: false })
        .order('display_order', { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      alert('이미지를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('이 이미지를 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('prayer_images')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      alert('삭제되었습니다.');
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleDeleteWeek = async (weekDate: string) => {
    if (!confirm(`${new Date(weekDate).toLocaleDateString('ko-KR')} 주간의 모든 이미지를 삭제하시겠습니까?`)) return;

    try {
      const { error } = await supabase
        .from('prayer_images')
        .delete()
        .eq('week_date', weekDate);

      if (error) throw error;
      
      alert('삭제되었습니다.');
      fetchImages();
    } catch (error) {
      console.error('Error deleting week images:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleSuccess = () => {
    fetchImages();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="수요910 기도제목 관리" subtitle="기도 이미지를 관리합니다" />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-12">
        {/* 안내 메시지 */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-6 sm:mb-8">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-lg sm:text-xl text-amber-600 mt-0.5"></i>
            <div>
              <h3 className="font-semibold text-amber-800 text-sm sm:text-base">이미지 업로드 안내</h3>
              <p className="text-xs sm:text-sm text-amber-700 mt-1">
                매주 수요일 기도제목 이미지를 8~10장 업로드해주세요. 
                업로드된 이미지는 홈페이지에 슬라이드로 표시됩니다.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Form Section */}
          <div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-8">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2F4F4F] rounded-lg flex items-center justify-center">
                  <i className="ri-upload-cloud-line text-lg sm:text-xl text-white"></i>
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  이미지 업로드
                </h2>
              </div>
              <ImageUploadForm onSuccess={handleSuccess} />
            </div>
          </div>

          {/* List Section */}
          <div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-gallery-line text-lg sm:text-xl text-white"></i>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    업로드된 이미지
                  </h2>
                </div>
                <span className="text-xs sm:text-sm text-gray-500">
                  총 {images.length}장
                </span>
              </div>
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-[#2F4F4F] rounded-full animate-spin"></div>
                  <p className="text-gray-500 mt-4 text-sm">불러오는 중...</p>
                </div>
              ) : (
                <ImageList
                  images={images}
                  onDelete={handleDelete}
                  onDeleteWeek={handleDeleteWeek}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
